import { Button, Dialog, DialogActions, DialogContent, Stack } from "@mui/material";
import ClosableDialogTitle from "common/ClosableDialogTitle";
import * as Yup from "yup";
import { Field as FormikField, Formik, FormikHelpers } from "formik";
import { DateTime } from "luxon";
import PrsDatePickerField from "common/FormikFields/PrsDatePickerField";
import { TextField as FmuiTextField } from "formik-mui";
import { LoadingButton } from "@mui/lab";
import { Validations } from "../common/validations/common-yup-validations";
import { useMutation } from "@apollo/client";
import {
  AddCommitteeMeetingMutation,
  AvailableMeetingsQuery,
  CommitteeMeeting,
  CommitteeMeetingInput,
  FetchCommitteeMeetingsQuery,
} from ".";
import { useNotifications } from "../notifications";
import { PracticeReview } from "../practice-reviews";
import { GetInasForUserQuery } from "../inas";
import { useCalendar } from "../util/CalendarProvider";
import { AppRoute } from "../Routes";
import { useHistory } from "react-router-dom";
import StackedStaticDataDisplay from "common/StackedStaticDataDisplay";

interface Props {
  handleClose: (prsToReassign?: PracticeReview[]) => void;
  registrationCommittee: boolean;
}

interface FormValues {
  meetingDate: DateTime | null;
  location: string;
  reviewQuota: number;
  subcommitteeDate: DateTime | null;
}

const AddMeetingDialog = (props: Props) => {
  const notifications = useNotifications();
  const { configureForBusinessDaysMath } = useCalendar();
  const history = useHistory();

  const [addMeetingMutate, addMeetingMutation] = useMutation<
    { committeeMeeting: { add: CommitteeMeeting } },
    { meeting: CommitteeMeetingInput }
  >(AddCommitteeMeetingMutation, {
    refetchQueries: [
      {
        query: FetchCommitteeMeetingsQuery,
        variables: { excludeFuture: false, pastPeriod: null, registrationCommittee: props.registrationCommittee },
      },
      { query: GetInasForUserQuery },
      { query: AvailableMeetingsQuery },
    ],
  });

  async function addMeeting(values: FormValues, actions: FormikHelpers<FormValues>) {
    const meetingInput = getMeetingInputFromFormValues(values);

    const result = await addMeetingMutate({
      variables: { meeting: meetingInput },
    });

    actions.setSubmitting(false);
    props.handleClose();

    if (result.data?.committeeMeeting?.add.id) {
      notifications.success("Added meeting.");
      if (props.registrationCommittee) {
        history.push(AppRoute.RegistrationCommitteeMeeting.replace(":id", result.data.committeeMeeting.add.id.toString()));
      } else {
        history.push(AppRoute.PRCommitteeMeeting.replace(":id", result.data.committeeMeeting.add.id.toString()));
      }
    }
  }

  function getMeetingInputFromFormValues(values: FormValues): CommitteeMeetingInput {
    return {
      meetingDate: values.meetingDate?.toISODate() || "",
      location: values.location,
      isRegistrationCommittee: props.registrationCommittee,
      reviewQuota: Number(values.reviewQuota),
      subcommitteeDate: !props.registrationCommittee ? (values.subcommitteeDate?.toISODate() ?? null) : null,
    };
  }

  const validationSchema = Yup.object({
    meetingDate: Validations.requiredDate(),
    location: Validations.requiredText(),
    reviewQuota: Validations.requiredPositiveInteger(),
  });

  const initialValues: FormValues = {
    meetingDate: null,
    location: "",
    reviewQuota: 60,
    subcommitteeDate: null,
  };

  return (
    <Dialog open={true} fullWidth maxWidth="sm">
      <ClosableDialogTitle onClose={props.handleClose}>Add Committee Meeting</ClosableDialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          await addMeeting(values, actions);

          props.handleClose();
        }}
        validationSchema={validationSchema}
      >
        {(formikProps) => (
          <>
            <DialogContent>
              <Stack spacing={3}>
                <StackedStaticDataDisplay
                  label="Meeting Type"
                  value={props.registrationCommittee ? "Registration Committee" : "Practice Review"}
                />
                <FormikField
                  component={PrsDatePickerField}
                  name="meetingDate"
                  label="Date"
                  fullWidth
                  required
                  minDate={DateTime.local()}
                  onChange={(newDate: DateTime | null) => {
                    formikProps.setValues({
                      ...formikProps.values,
                      meetingDate: newDate,
                      subcommitteeDate: newDate ? configureForBusinessDaysMath(newDate).minusBusiness({ days: 2 }) : null,
                    });
                  }}
                />
                <FormikField
                  component={FmuiTextField}
                  name="location"
                  label="Location"
                  fullWidth
                  required
                  helperText={`(Tip: Include the text "${
                    props.registrationCommittee ? "Registrar" : "Zone 1"
                  }" if this should not be a stand-alone meeting.)`}
                />
                <FormikField component={FmuiTextField} name="reviewQuota" label="Maximum to Assign" fullWidth required />
                {!props.registrationCommittee && (
                  <FormikField
                    component={PrsDatePickerField}
                    name="subcommitteeDate"
                    label="Subcommittee Meeting Date"
                    fullWidth
                    minDate={DateTime.local()}
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => props.handleClose()} disabled={addMeetingMutation.loading}>
                Cancel
              </Button>
              <LoadingButton
                color="primary"
                variant="contained"
                loading={addMeetingMutation.loading}
                onClick={() => formikProps.submitForm()}
              >
                Add
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddMeetingDialog;
